import { CanvasElement } from './engineTypes';

export interface SceneDynamics {
  // Composition
  horizontalFraming: string;
  verticalDominance: string;
  // Distances
  cameraCharacterDistance: string;
  cameraCharacterDistanceDesc: string;
  lightCharacterDistance: string;
  lightCharacterDistanceDesc: string;
  // Spatial descriptors
  spatialRelationships: string[];
}

function dist(a: { x: number; y: number }, b: { x: number; y: number }): number {
  return Math.sqrt((a.x - b.x) ** 2 + (a.y - b.y) ** 2);
}

function horizontalFraming(x: number): string {
  if (x < 25) return 'off-center left composition';
  if (x < 40) return 'left-weighted asymmetrical framing';
  if (x <= 60) return 'balanced centered composition';
  if (x <= 75) return 'right-weighted asymmetrical framing';
  return 'off-center right framing';
}

function verticalDominance(y: number): string {
  if (y < 25) return 'dominant upper framing, towering presence';
  if (y < 40) return 'slightly elevated perspective';
  if (y <= 60) return 'eye-level natural perspective';
  if (y <= 75) return 'slightly low angle';
  return 'low-angle heroic framing, imposing stature';
}

function cameraSubjectDistance(d: number): { label: string; desc: string } {
  if (d < 15) return { label: 'intimate', desc: 'extreme close-up intimate portrait, every detail visible' };
  if (d < 25) return { label: 'close', desc: 'close-up portrait with sharp subject focus' };
  if (d < 40) return { label: 'medium_close', desc: 'medium close-up with upper body emphasis' };
  if (d < 55) return { label: 'medium', desc: 'medium shot with balanced framing' };
  if (d < 70) return { label: 'medium_wide', desc: 'medium-wide shot establishing spatial context' };
  if (d < 85) return { label: 'wide', desc: 'wide shot with full environmental context' };
  return { label: 'establishing', desc: 'wide establishing shot, subject within grand environment' };
}

function lightSubjectDistance(d: number): { label: string; desc: string } {
  if (d < 15) return { label: 'intense', desc: 'intense concentrated highlight with sharp shadows' };
  if (d < 30) return { label: 'strong', desc: 'strong directional illumination with defined shadows' };
  if (d < 50) return { label: 'moderate', desc: 'moderate balanced lighting with gentle falloff' };
  if (d < 70) return { label: 'soft', desc: 'soft diffused ambient light wrapping around subject' };
  return { label: 'distant', desc: 'distant atmospheric ambient diffusion, subtle environmental glow' };
}

export function calculateSceneDynamics(elements: CanvasElement[]): SceneDynamics {
  const cameras = elements.filter(e => e.type === 'camera');
  const characters = elements.filter(e => e.type === 'character');
  const lights = elements.filter(e => e.type === 'light');

  const dynamics: SceneDynamics = {
    horizontalFraming: 'balanced centered composition',
    verticalDominance: 'eye-level natural perspective',
    cameraCharacterDistance: 'medium',
    cameraCharacterDistanceDesc: '',
    lightCharacterDistance: 'moderate',
    lightCharacterDistanceDesc: '',
    spatialRelationships: [],
  };

  // Primary character position determines framing
  if (characters.length > 0) {
    const mainChar = characters[0];
    dynamics.horizontalFraming = horizontalFraming(mainChar.x);
    dynamics.verticalDominance = verticalDominance(mainChar.y);
  }

  // Camera-Character distance
  if (cameras.length > 0 && characters.length > 0) {
    const d = dist(cameras[0], characters[0]);
    const result = cameraSubjectDistance(d);
    dynamics.cameraCharacterDistance = result.label;
    dynamics.cameraCharacterDistanceDesc = result.desc;
  }

  // Light-Character distance
  if (lights.length > 0 && characters.length > 0) {
    const d = dist(lights[0], characters[0]);
    const result = lightSubjectDistance(d);
    dynamics.lightCharacterDistance = result.label;
    dynamics.lightCharacterDistanceDesc = result.desc;
  }

  // Multi-character spatial relationships
  if (characters.length > 1) {
    for (let i = 0; i < characters.length - 1; i++) {
      for (let j = i + 1; j < characters.length; j++) {
        const d = dist(characters[i], characters[j]);
        const rel = d < 20 ? 'intimately close together' : d < 40 ? 'near each other' : d < 60 ? 'at moderate distance' : 'far apart across the frame';
        dynamics.spatialRelationships.push(`${characters[i].properties.subject || 'subject'} and ${characters[j].properties.subject || 'subject'} ${rel}`);
      }
    }
  }

  // Light position relative to camera (lighting direction)
  if (lights.length > 0 && cameras.length > 0) {
    const l = lights[0];
    const c = cameras[0];
    const angle = Math.atan2(l.y - c.y, l.x - c.x) * (180 / Math.PI);
    let dir = '';
    if (angle >= -22.5 && angle < 22.5) dir = 'side lighting from the right';
    else if (angle >= 22.5 && angle < 67.5) dir = 'low side lighting';
    else if (angle >= 67.5 && angle < 112.5) dir = 'underlighting';
    else if (angle >= 112.5 && angle < 157.5) dir = 'low side lighting from left';
    else if (angle >= 157.5 || angle < -157.5) dir = 'side lighting from the left';
    else if (angle >= -157.5 && angle < -112.5) dir = 'top-left accent lighting';
    else if (angle >= -112.5 && angle < -67.5) dir = 'overhead lighting';
    else dir = 'top-right accent lighting';
    dynamics.spatialRelationships.push(dir);
  }

  return dynamics;
}
